import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { t } from "@lingui/core/macro";
import { pick } from "ramda";

import { SubmitCancelButtons } from "_/components/modal";
import { FormSelect, Option } from "_/components/select";
import { FormTextField } from "_/components/text-field";
import { FormField } from "_/components/form-field";
import { useAddToast } from "_/components/toasts";
import { Form } from "_/components/form";

import { Hardware, Machine, useEditMachine } from "_/data/machines";
import { useChannels } from "_/data/channels";
import { useBuildsets, Buildset, buildBuildsetIdMap } from "_/data/buildsets";

import { Uuid } from "_/types";

import { formatBuildsetLabel } from "_/utils/buildset-display";
import { dateOnlyString } from "_/utils/datetime";

function getBuildsetOptions(buildsets: Buildset[]): Option<Uuid | null>[] {
  return [
    { label: t`common.not-pinned`, value: "", data: null },
    ...buildsets.map((bs) => ({
      label: formatBuildsetLabel(bs).value,
      value: bs.id,
      data: bs.id,
    })),
  ];
}

function getChannelOptions(channels: { name: string }[]): Option<string>[] {
  return channels.map((ch) => ({
    value: ch.name,
    label: ch.name,
  }));
}

export const MachineEditForm = ({
  machine,
  onClose,
}: {
  machine: Machine | undefined;
  onClose: () => void;
}) => {
  const editMachine = useEditMachine();
  const { data: channels = [] } = useChannels();
  const { data: buildsets = [] } = useBuildsets();
  const buildsetIdMap = buildBuildsetIdMap(buildsets);

  const addToast = useAddToast();

  const defaultValues = machine
    ? {
        ...pick(["updateChannel", "buildsetPin", "pinReason"], machine),
        ...pick(["serial", "manufactureDate"], machine.hardware),
      }
    : {
        updateChannel: "",
        buildsetPin: "",
        pinReason: "",
        serial: "",
        manufactureDate: dateOnlyString({ value: new Date() }),
      };

  const channelOptions = getChannelOptions(channels);
  const buildsetOptions = getBuildsetOptions(buildsets);

  const currentBuildset =
    typeof machine?.metadata?.buildsetId === "string"
      ? buildsetIdMap[machine.metadata.buildsetId]
      : undefined;

  const buildsetLabel = currentBuildset
    ? formatBuildsetLabel(currentBuildset).value
    : "--";

  type FormData = Pick<Hardware, "serial" | "manufactureDate"> &
    Pick<Machine, "updateChannel" | "buildsetPin" | "pinReason">;

  const {
    control,
    handleSubmit,
    watch,
    clearErrors,
    formState: { isDirty },
  } = useForm<FormData>({ defaultValues });

  const currentBuildsetPin = watch("buildsetPin");

  useEffect(() => {
    if (!currentBuildsetPin) clearErrors("pinReason");
  }, [currentBuildsetPin, clearErrors]);

  const onSubmit = handleSubmit(async (data: FormData) => {
    if (!machine) {
      throw new Error("Machine data is missing (unreachable).");
    }

    const { updateChannel, buildsetPin, pinReason } = data;

    await editMachine.mutateAsync({
      id: machine.id,
      params: {
        updateChannel,
        buildsetPin: buildsetPin || null,
        pinReason: pinReason ?? undefined,
      },
      onSuccess: onClose,
      onError: ({ statusCode: rawStatusCode, message }) => {
        const statusCode = rawStatusCode ?? "unknown";
        const msg = message || t`common.http-error-code ${statusCode}`;
        const title =
          rawStatusCode === 403
            ? t`components.admin-view.action-permission-denied`
            : t`components.admin-view.machine-update-failed ${msg}`;
        addToast({
          title,
          kind: "warning",
          timeout: 5_000,
        });
      },
    });
  });

  return (
    <Form onSubmit={onSubmit}>
      <FormField label={t`common.serial-number`}>
        {machine?.hardware.serial}
      </FormField>
      <FormField label={t`common.machine-manufacture-date`}>
        {machine?.hardware.manufactureDate}
      </FormField>

      <FormSelect
        control={control}
        name={"updateChannel"}
        label={t`common.software-channel`}
        options={channelOptions}
        isSearchable={false}
      />
      <FormSelect
        control={control}
        name="buildsetPin"
        label={t`common.pinned-software-version`}
        options={[...buildsetOptions]}
        isSearchable={true}
      />
      <FormTextField
        control={control}
        name="pinReason"
        label={t`common.pin-reason`}
        placeholder={t`common.pin-reason-placeholder`}
        disabled={!currentBuildsetPin}
        rules={{
          required: currentBuildsetPin ? t`common.pin-reason-required` : false,
        }}
      />

      <FormField label={t`common.current-software-version`}>
        {buildsetLabel}
      </FormField>

      <SubmitCancelButtons onCancel={onClose} disableSubmit={!isDirty} />
    </Form>
  );
};

export { getBuildsetOptions, getChannelOptions };
